import { BigNumber, ethers } from 'ethers'
import { useEffect, useMemo, useState } from 'react'
import { useWallet } from '~/lib/useWallet'
import { CONFIG } from './config'

// The minimum ABI to get ERC20 Token balance
const erc20Abi = [
	{
		constant: true,
		inputs: [{ name: '_owner', type: 'address' }],
		name: 'balanceOf',
		outputs: [{ name: 'balance', type: 'uint256' }],
		type: 'function',
	},
	{
		constant: true,
		inputs: [],
		name: 'decimals',
		outputs: [{ name: '', type: 'uint8' }],
		type: 'function',
	},
]

export function useGetSisBalance(): BigNumber | null {
	const { web3, account, chainId } = useWallet()

	const [balance, setBalance] = useState<BigNumber | null>(null)

	const sisAddress = useMemo(() => {
		const chain = CONFIG.chains.find((chain) => chain.id === chainId)

		if (!chain) {
			return null
		}

		return chain.sisAddress ?? null
	}, [chainId])

	useEffect(() => {
		if (!web3 || !account || !sisAddress) {
			setBalance(null)
			return
		}

		const contract = new ethers.Contract(sisAddress, erc20Abi, web3)

		let ignore = false

		const listener = () => {
			contract
				.balanceOf(account)
				.then((balance: BigNumber) => {
					if (ignore) {
						return
					}

					setBalance((currentBalance) => {
						if (currentBalance && currentBalance.eq(balance)) {
							return currentBalance
						}

						return balance
					})
				})
				.catch(() => {
					return BigNumber.from(0)
				})
		}

		listener()

		web3.on('block', listener)

		return () => {
			web3.off('block', listener)
			ignore = true
		}
	}, [web3, account, sisAddress, chainId])

	return balance
}
